import React, { useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';
import { NumericFormat } from 'react-number-format';
import Datepicker from 'react-tailwindcss-datepicker';
import { toast } from 'react-hot-toast';
import { updateCampaignFundraising } from '~/services/campaign';

interface EditFundraisingModalProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId: string | number;
    initialThermStartDate: string | null | undefined;
    initialGoal: number | string | null | undefined;
    onSaved: () => void;
}

type DatepickerValue = { startDate: Date | string | null; endDate: Date | string | null };

const toDatepickerValue = (iso: string | null | undefined): DatepickerValue => {
    if (!iso) return { startDate: null, endDate: null };
    return { startDate: iso, endDate: iso };
};

const toIsoDate = (value: DatepickerValue): string | null => {
    if (!value?.startDate) return null;
    if (typeof value.startDate === 'string') return value.startDate;
    const d = value.startDate as Date;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const EditFundraisingModal: React.FC<EditFundraisingModalProps> = ({
    isOpen,
    onClose,
    campaignId,
    initialThermStartDate,
    initialGoal,
    onSaved,
}) => {
    const [thermStartDate, setThermStartDate] = useState<DatepickerValue>(
        toDatepickerValue(initialThermStartDate ?? null)
    );
    const [goal, setGoal] = useState<string>(
        initialGoal != null ? String(parseInt(String(initialGoal), 10) || 0) : ''
    );
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setThermStartDate(toDatepickerValue(initialThermStartDate ?? null));
            setGoal(
                initialGoal != null
                    ? String(parseInt(String(initialGoal), 10) || 0)
                    : ''
            );
        }
    }, [isOpen, initialThermStartDate, initialGoal]);

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27 && !isSaving) onClose();
        };
        if (isOpen) document.addEventListener('keydown', keyHandler);
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose, isSaving]);

    if (!isOpen) return null;

    const handleSave = async () => {
        const goalNum = goal === '' ? 0 : parseInt(goal, 10);
        if (Number.isNaN(goalNum) || goalNum < 0) {
            toast.error('Goal must be zero or a positive number');
            return;
        }

        setIsSaving(true);
        try {
            await updateCampaignFundraising(campaignId, {
                therm_start_date: toIsoDate(thermStartDate),
                goal: goalNum,
            });
            toast.success('Fundraising info updated');
            onSaved();
            onClose();
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update fundraising info');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div
            className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'
            onClick={() => !isSaving && onClose()}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                    onClick={onClose}
                    disabled={isSaving}
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <h2 className='mb-[24px] text-[22px] font-bold'>
                        Edit Fundraising
                    </h2>

                    <div className='mb-[20px]'>
                        <label className='mb-[6px] block text-[12px]/[16px] text-[#949599]'>
                            Thermometer Start Date
                        </label>
                        <Datepicker
                            inputClassName='w-full rounded border border-stroke px-[12px] py-[8px] text-sm focus:border-primary focus:outline-none'
                            value={thermStartDate}
                            asSingle={true}
                            useRange={false}
                            onChange={(newValue) => {
                                if (newValue) setThermStartDate(newValue as DatepickerValue);
                            }}
                            showShortcuts={false}
                            placeholder='Select date or leave empty'
                        />
                    </div>

                    <div className='mb-[30px]'>
                        <label className='mb-[6px] block text-[12px]/[16px] text-[#949599]'>
                            Fundraising Goal
                        </label>
                        <NumericFormat
                            value={goal}
                            thousandSeparator=','
                            decimalScale={0}
                            allowNegative={false}
                            prefix={'$'}
                            placeholder='$0'
                            className='w-full rounded border border-stroke px-[12px] py-[8px] text-sm focus:border-primary focus:outline-none'
                            onValueChange={({ value }) => setGoal(value)}
                        />
                    </div>

                    <div className='flex justify-end gap-[12px]'>
                        <button
                            onClick={onClose}
                            disabled={isSaving}
                            className='rounded-full border-[2px] px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className='rounded-full bg-primary px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-white transition-colors duration-500 hover:bg-opacity-90 disabled:opacity-50 disabled:cursor-not-allowed'
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditFundraisingModal;
